import imagePlaceholder from '@/assets/placeholder.png';
import Modal from '@/components/Modal';
import Item from '@/models/Item';
import { MediaType } from '@/models/MediaType';
import { ApplicationState } from '@/store';
import {
    archiveItemRequest,
    deleteItemRequest,
    publishItemRequest,
    toggleItemVisibilityClear,
    toggleItemVisibilityRequest,
} from '@/store/item/action';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import Switch from 'react-switch';
import './style.scss';

interface ItemCardProps {
    item: Item;
}

const ItemCard = ({ item }: ItemCardProps) => {
    const [icon, setIcon] = useState('');
    const [alertText, setAlertText] = useState('');
    const [action, setAction] = useState('');
    const [visibility, setVisibility] = useState(false);
    const { data: strings } = useSelector((state: ApplicationState) => state.locale);
    const { hasError } = useSelector((state: ApplicationState) => state.itens);
    const {
        data: { colors },
    } = useSelector((state: ApplicationState) => state.appOrganization);
    const { data: identity } = useSelector((state: ApplicationState) => state.identity);

    const dispatch = useDispatch();

    useEffect(() => {
        setVisibility(item.visibility);

        if (hasError) {
            setVisibility(!item.visibility);
            dispatch(toggleItemVisibilityClear());
        }
    }, [dispatch, hasError, item.visibility]);

    const { status, id } = item;

    const handleModalChange = (icon: string, text: string, action: string) => {
        setIcon(icon);
        setAlertText(text);
        setAction(action);
    };

    const renderTitle = (type: MediaType) => {
        switch (type) {
            case 'HTML':
                return strings.ARTICLE;
            case 'AUDIO':
                return strings.AUDIO;
            case 'EXTERNAL':
                return strings.LINK;
            case 'VIDEO':
                return strings.VIDEO;
            default:
                return strings.ITEM;
        }
    };

    const renderLanguage = (type: string) => {
        switch (type) {
            case 'pt':
                return 'PT-BR';
            case 'en':
                return 'ENG';
            default:
                return '';
        }
    };

    const renderActions = () => {
        switch (status) {
            case 'PUBLISHED':
                return (
                    <>
                        <Link className="dropdown-content" to={`/items/${id}/edit`}>
                            <i className="material-icons va-t m-right--xxs">edit</i>
                            <span>{strings.EDIT}</span>
                        </Link>
                        <div
                            className="dropdown-content"
                            data-target={`#alert-modal-${id}`}
                            data-toggle="modal"
                            onClick={() => handleModalChange('how_to_vote', strings.ARCHIVE_ITEM_CONFIRMATION, 'archive')}
                            onKeyDown={() => {}}
                            role="button"
                            tabIndex={0}
                        >
                            <i className="material-icons-outlined va-t m-right--xxs">how_to_vote</i>
                            <span>{strings.ARCHIVE_ITEM}</span>
                        </div>
                    </>
                );
            case 'DRAFT':
                return (
                    <>
                        <Link className="dropdown-content" to={`/items/${id}/edit`}>
                            <i className="material-icons va-t m-right--xxs">edit</i>
                            <span>{strings.EDIT}</span>
                        </Link>
                        <div
                            className="dropdown-content"
                            data-target={`#alert-modal-${id}`}
                            data-toggle="modal"
                            onClick={() => handleModalChange('outlined_flag', strings.PUBLISH_ITEM_CONFIRMATION, 'publish')}
                            onKeyDown={() => {}}
                            role="button"
                            tabIndex={0}
                        >
                            <i className="material-icons-outlined va-t m-right--xxs">outlined_flag</i>
                            <span>{strings.PUBLISH}</span>
                        </div>
                        <div
                            className="dropdown-content"
                            data-target={`#alert-modal-${id}`}
                            data-toggle="modal"
                            onClick={() => handleModalChange('delete', strings.DELETE_ITEM_CONFIRMATION, 'delete')}
                            onKeyDown={() => {}}
                            role="button"
                            tabIndex={0}
                        >
                            <i className="material-icons-outlined va-t m-right--xxs">delete</i>
                            <span>{strings.REMOVE}</span>
                        </div>
                    </>
                );
            case 'ARCHIVED':
                return (
                    <div
                        className="dropdown-content"
                        data-target={`#alert-modal-${id}`}
                        data-toggle="modal"
                        onClick={() => handleModalChange('undo', strings.UNARCHIVE_ITEM_CONFIRMATION, 'publish')}
                        onKeyDown={() => {}}
                        role="button"
                        tabIndex={0}
                    >
                        <i className="material-icons-outlined va-t m-right--xxs">undo</i>
                        <span>{strings.UNARCHIVE}</span>
                    </div>
                );
            default:
                return null;
        }
    };

    const handleAction = () => {
        switch (action) {
            case 'archive':
                if (item.id) return dispatch(archiveItemRequest(item.id));
                break;
            case 'publish':
                if (item.id) return dispatch(publishItemRequest({ id: item.id, status: item.status }));
                break;
            case 'delete':
                if (item.id) return dispatch(deleteItemRequest(item.id));
                break;
            default:
                return null;
        }
    };

    const handleVisibility = () => {
        setVisibility((prevState) => !prevState);

        if (item.id) {
            dispatch(toggleItemVisibilityRequest(item.id));
        }
    };

    const formatWorkload = (value: number) => {
        const dur = moment.duration(value, 'minutes');
        const hours = Math.floor(dur.asHours());
        const mins = Math.floor(dur.asMinutes()) - hours * 60;
        const sec = Math.floor(dur.asSeconds()) - hours * 60 * 60 - mins * 60;

        const result = (hours ? hours + ':' : '') + mins + ':' + (sec > 9 ? sec : '0' + sec);
        return result;
    };

    return (
        <>
            <Modal
                content={alertText}
                handleLeftClick={handleAction}
                icon={icon}
                leftButtonText={strings.YES}
                rightButtonText={strings.NO}
                target={`alert-modal-${id}`}
            />
            <div className="d-flex item-card">
                <div className={`${item.status === 'ARCHIVED' ? 'item-archived' : ''}`}>
                    <img alt="" src={item.mainImage || imagePlaceholder} />
                </div>
                <div className={`d-flex flex-column justify-content-center item-card-content ${item.status === 'ARCHIVED' ? 'item-archived' : ''}`}>
                    <span className="item-type">{renderTitle(item.mediaType)}</span>
                    <span className="item-title">{item.name}</span>
                    <div>
                        <span className="item-info-key">{strings.AUTHOR}: </span>
                        <span className="item-info-value">{item.author}</span>
                        <span className="item-info-key">{strings.LANGUAGE}: </span>
                        <span className="item-info-value">{renderLanguage(item.locale || '')}</span>
                        <span className="item-info-key">{strings.ESTIMATED_TIME}: </span>
                        <span className="item-info-value">{item.workload && formatWorkload(item.workload)}</span>
                    </div>
                </div>
                <div className="item-actions">
                    {(item.organizationId !== null || identity.user.admin) && (
                        <div className="dropdown">
                            <i className="material-icons-outlined align-middle" data-toggle="dropdown" style={{ color: '#808080' }}>
                                more_horiz
                            </i>
                            <div className="dropdown-menu dropdown-menu-right">
                                <div className="triangle-container">
                                    <div className="dropdown-triangle" />
                                    <div className="dropdown-triangle-border" />
                                </div>
                                {renderActions()}
                            </div>
                        </div>
                    )}

                    {item.status === 'PUBLISHED' && (item.organizationId !== null || identity.user.admin) && (
                        <>
                            <span>{visibility ? strings.PUBLIC : strings.PRIVATE}</span>
                            <Switch
                                checked={visibility}
                                checkedIcon={false}
                                handleDiameter={20}
                                height={14}
                                offColor="#d1d1d1"
                                offHandleColor="#b3b3b3"
                                onChange={handleVisibility}
                                onColor={colors[0]}
                                onHandleColor={colors[0]}
                                uncheckedIcon={false}
                                width={30}
                            />
                        </>
                    )}
                </div>
            </div>
        </>
    );
};

export default ItemCard;
